"use client"

import { useState } from "react"
import { AlertTriangle } from "lucide-react"

import { cn } from "@/common/lib/utils"
import type { Curriculum, CurriculumItemReason } from "@/modules/lernpfad/engine"
import type { Translator } from "@/modules/lernpfad/i18n/ui"
import { MARK_GAP, REASON_ORDER, reasonFill, STATUS_VAR } from "./palette"

/**
 * Planned hours per quarter, stacked by reason, against the budget.
 *
 * Form choice: the data's job is MAGNITUDE-against-a-threshold over an ordered
 * sequence of quarters. A column chart with an explicit budget rule reads that
 * in one glance, where a bare "9 / 12.8 h" makes the reader do the comparison.
 * Over-budget quarters are the point of the view, so the rule is drawn.
 *
 * Built in HTML/flex rather than SVG deliberately. A first pass used a
 * `viewBox="0 0 100 168"` with `preserveAspectRatio="none"` so the chart would
 * fill its container — which scales x and y independently and therefore
 * stretched every label into unreadable smears and turned the rounded bar ends
 * into ellipses. HTML columns scale without distortion and render text natively.
 *
 * One axis. Hours is the single measure; the budget is a reference line in the
 * same unit, not a second scale.
 */

interface QuarterLoadChartProps {
    curriculum: Curriculum
    t: Translator
    reasonLabel: (reason: CurriculumItemReason) => string
}

const PLOT_HEIGHT = 132

export default function QuarterLoadChart({ curriculum, t, reasonLabel }: QuarterLoadChartProps) {
    const [hover, setHover] = useState<number | null>(null)

    const quarters = curriculum.quarters
    if (quarters.length === 0) return null

    const budget = curriculum.budgetHoursPerQuarter
    const peak = Math.max(budget, ...quarters.map((quarter) => quarter.plannedHours))
    // Headroom so an over-budget column never touches the top edge.
    const scaleMax = peak * 1.15 || 1
    const toPx = (hours: number) => (hours / scaleMax) * PLOT_HEIGHT

    const present = REASON_ORDER.filter((reason) =>
        quarters.some((quarter) => quarter.items.some((item) => item.reason === reason)),
    )

    const hovered = hover !== null ? quarters[hover] : null

    return (
        <figure className="m-0">
            <div className="relative" style={{ height: PLOT_HEIGHT + 34 }}>
                {/* Budget rule, drawn across the full plot */}
                <div
                    className="pointer-events-none absolute inset-x-0 border-t border-dashed"
                    style={{ bottom: 34 + toPx(budget), borderColor: STATUS_VAR.grid }}
                >
                    <span className="absolute -top-4 right-0 text-[10px] text-muted-foreground">
                        {t("levers.budget", { hours: budget })}
                    </span>
                </div>

                {/* Baseline */}
                <div
                    className="pointer-events-none absolute inset-x-0 border-t"
                    style={{ bottom: 34, borderColor: STATUS_VAR.grid }}
                />

                <div className="absolute inset-0 flex items-end">
                    {quarters.map((quarter, index) => {
                        const overBudget = quarter.plannedHours > budget
                        const isHovered = hover === index

                        const segments = present
                            .map((reason) => {
                                const hours = quarter.items
                                    .filter((item) => item.reason === reason)
                                    .reduce((sum, item) => sum + item.baustein.durationHours, 0)
                                return hours > 0 ? { reason, hours } : null
                            })
                            .filter((segment): segment is NonNullable<typeof segment> => segment !== null)

                        return (
                            <div
                                key={quarter.index}
                                // `relative` is load-bearing: the label band below is
                                // absolutely positioned, and without a positioned
                                // parent it anchors to the whole plot instead of to
                                // its own column — every label then drifts away from
                                // the bar it names.
                                className="relative flex h-full flex-1 flex-col justify-end"
                                onMouseEnter={() => setHover(index)}
                                onMouseLeave={() => setHover(null)}
                            >
                                {/* Stack, bottom-up in fixed reason order */}
                                <div
                                    className={cn(
                                        "mx-auto flex w-9 flex-col-reverse overflow-hidden rounded-t transition-opacity",
                                        hover !== null && !isHovered && "opacity-55",
                                    )}
                                    style={{ marginBottom: 34, gap: MARK_GAP }}
                                >
                                    {segments.map((segment) => (
                                        <div
                                            key={segment.reason}
                                            style={{
                                                height: Math.max(3, toPx(segment.hours)),
                                                background: reasonFill(segment.reason),
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Axis labels, in the reserved 34px band */}
                                <div className="absolute bottom-0 flex w-full flex-col items-center gap-0.5">
                                    <span className="text-[10px] text-muted-foreground">
                                        {quarter.label}
                                    </span>
                                    <span
                                        className={cn(
                                            "inline-flex items-center gap-0.5 text-[11px] tabular-nums",
                                            overBudget
                                                ? "font-semibold text-destructive"
                                                : "text-muted-foreground",
                                        )}
                                    >
                                        {overBudget && <AlertTriangle className="size-2.5" />}
                                        {quarter.plannedHours} h
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* Tooltip */}
                {hovered && hovered.items.length > 0 && (
                    <div
                        className="pointer-events-none absolute top-0 z-10 min-w-[160px] rounded-md border bg-popover p-2 text-xs shadow-md"
                        style={{
                            left: `${((hover! + 0.5) / quarters.length) * 100}%`,
                            transform: "translateX(-50%)",
                        }}
                    >
                        <p className="font-medium">{hovered.label}</p>
                        <p className="mt-0.5 text-muted-foreground">
                            {t("employee.quarterBudget", {
                                planned: hovered.plannedHours,
                                budget: hovered.budgetHours,
                            })}
                        </p>
                        <div className="mt-1.5 space-y-0.5">
                            {REASON_ORDER.map((reason) => {
                                const hours = hovered.items
                                    .filter((item) => item.reason === reason)
                                    .reduce((sum, item) => sum + item.baustein.durationHours, 0)
                                if (hours === 0) return null
                                return (
                                    <div key={reason} className="flex items-center gap-1.5">
                                        <span
                                            className="size-2 shrink-0 rounded-[2px]"
                                            style={{ background: reasonFill(reason) }}
                                        />
                                        <span className="flex-1 text-muted-foreground">
                                            {reasonLabel(reason)}
                                        </span>
                                        <span className="tabular-nums">{hours} h</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Legend — always present for >= 2 series. */}
            {present.length >= 2 && (
                <figcaption className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2">
                    {present.map((reason) => (
                        <span
                            key={reason}
                            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
                        >
                            <span
                                className="size-2 rounded-[2px]"
                                style={{ background: reasonFill(reason) }}
                            />
                            {reasonLabel(reason)}
                        </span>
                    ))}
                </figcaption>
            )}
        </figure>
    )
}
